import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Button,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import axios from "axios";
import { buildStatusApiUrl } from "./statusApiConfig";
import Lp006ContractForm from "./components/contracts/Lp006ContractForm";
import { isLp006Contract } from "./components/contracts/lp006Contract";

const LegacyCreareContractForm = ({ contractData, handleInputChange, issueId }) => {
  const numeTitularCont = `${contractData.nume} ${contractData.prenume}`.trim();
  const insotitDisplay = Array.isArray(contractData.checkboxOptions)
    ? contractData.checkboxOptions.filter(Boolean).join(", ")
    : contractData.checkboxOptions || "";
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localContCurent, setLocalContCurent] = useState(
    contractData.contCurent || "",
  );
  const [localNumeTitularCont, setLocalNumeTitularCont] = useState(
    `${contractData.nume} ${contractData.prenume}`.trim() || "",
  );
  const [localBanca, setLocalBanca] = useState(contractData.banca || "");
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const retryInterval = 2000;
  const maxRetries = 12;
  const [localPretFinalVanzare, setLocalPretFinalVanzare] = useState(
    contractData.pretFinalVanzare || "",
  );
  const [localDataPlataFinalVanzare, setLocalDataPlataFinalVanzare] = useState(
    contractData.dataPlataFinalVanzare || "",
  );
  const [localPretCuvenit, setLocalPretCuvenit] = useState(
    contractData.pretCuvenit || "",
  );
  const [localCategoria, setLocalCategoria] = useState(
    contractData.categoria || "",
  );
  const [localBrand, setLocalBrand] = useState(contractData.brand || "");
  const [localModel, setLocalModel] = useState(contractData.model || "");
  const [contractTier, setContractTier] = useState("basic");
  const [didToggleTier, setDidToggleTier] = useState(false);
  const bankIbanPrefillRef = useRef({ issueId: null, applied: false });

  useEffect(() => {
    setLocalPretFinalVanzare(contractData.pretFinalVanzare || "");
    setLocalDataPlataFinalVanzare(contractData.dataPlataFinalVanzare || "");
  }, [contractData.pretFinalVanzare, contractData.dataPlataFinalVanzare]);

  useEffect(() => {
    if (!issueId) return;

    if (bankIbanPrefillRef.current.issueId !== issueId) {
      bankIbanPrefillRef.current = { issueId, applied: false };
    }

    if (bankIbanPrefillRef.current.applied) return;

    const fetchBankIban = async () => {
      try {
        const response = await axios.get(
          `https://server-9p4hx.kinsta.app/api/tasks/jira-task/${issueId}`,
        );
        const jiraBank = response.data?.customfield_10175 || "";
        const jiraIban = response.data?.customfield_10176 || "";

        setLocalContCurent(jiraIban);
        handleInputChange({ target: { name: "contCurent", value: jiraIban } });
        setLocalBanca(jiraBank);
        handleInputChange({ target: { name: "banca", value: jiraBank } });
        bankIbanPrefillRef.current.applied = true;
      } catch (err) {
        console.warn("[JIRA] Eroare la preluarea bancii/IBAN:", err);
      }
    };

    fetchBankIban();
  }, [issueId, handleInputChange]);

  // La efectele de sincronizare:
  useEffect(() => {
    console.log("Pret Cuvenit:", contractData.pretCuvenit);
    console.log("Brand:", contractData.brand);
    console.log("Model:", contractData.model);
    console.log("Categoria:", contractData.categoria);

    setLocalPretCuvenit(contractData.pretCuvenit || "");
    setLocalCategoria(contractData.categoria || "");
    setLocalBrand(contractData.brand || "");
    setLocalModel(contractData.model || "");
  }, [
    contractData.pretCuvenit,
    contractData.categoria,
    contractData.brand,
    contractData.model,
  ]);

  // Funcție pentru reîncercarea fetch-ului
  const fetchContractStatus = async (retriesLeft) => {
    try {
      const numarContract = contractData.numarContract || "";
      const lastFiveCharacters = numarContract.slice(-5);

      if (!lastFiveCharacters) {
        if (retriesLeft > 0) {
          console.log(
            `NumarContract indisponibil. Retries rămase: ${retriesLeft}`,
          );
          setTimeout(() => fetchContractStatus(retriesLeft - 1), retryInterval);
          return;
        } else {
          // Nu afișăm eroarea în UI
          console.warn(
            "Numărul contractului nu a fost primit după multiple încercări.",
          );
          return;
        }
      }

      console.log(
        `Ultimele 5 caractere din numarContract: ${lastFiveCharacters}`,
      );
      const response = await axios.get(
        `https://server-9p4hx.kinsta.app/api/tasks/cont-status/${lastFiveCharacters}`,
      );
      console.log("Răspuns de la backend:", response.data);

      if (response.data && response.data.status) {
        setStatus(response.data.status);
        setError(null); // Resetează eroarea dacă fetch-ul are succes
        console.log(`Status extras din răspuns: ${response.data.status}`);
      } else {
        throw new Error("Statusul nu este prezent în răspunsul backend.");
      }
    } catch (error) {
      console.error("Eroare la obținerea statusului:", error.message);
      if (retriesLeft > 0) {
        console.log(`Retry rămase: ${retriesLeft}`);
        setTimeout(() => fetchContractStatus(retriesLeft - 1), retryInterval);
      } else {
        // Nu afișăm eroarea în UI
        console.warn(
          "Nu s-a putut obține statusul contractului după mai multe încercări.",
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Sincronizăm stările locale cu `contractData` atunci când se schimbă extern
  useEffect(() => {
    setLocalContCurent(contractData.contCurent || "");
    setLocalNumeTitularCont(
      `${contractData.nume} ${contractData.prenume}`.trim() || "",
    );
    setLocalBanca(contractData.banca || "");
  }, [
    contractData.contCurent,
    contractData.nume,
    contractData.prenume,
    contractData.banca,
  ]);

  // Adăugarea a 5 zile la `dataDePlata`, ajustată pentru weekend, fără buclă infinită
  useEffect(() => {
    if (contractData.tipContract?.includes("LP004")) {
      const dataContract = new Date(contractData.dataContract);
      if (!isNaN(dataContract)) {
        const newDate = new Date(dataContract);
        newDate.setDate(dataContract.getDate() + 5); // Adăugăm 5 zile

        const dayOfWeek = newDate.getDay();
        if (dayOfWeek === 6) {
          newDate.setDate(newDate.getDate() + 2);
        } else if (dayOfWeek === 0) {
          newDate.setDate(newDate.getDate() + 1);
        }

        const formattedDate = newDate.toISOString().split("T")[0];

        // Previne bucla infinită: setează doar dacă e diferit
        if (contractData.dataDePlata !== formattedDate) {
          handleInputChange({
            target: { name: "dataDePlata", value: formattedDate },
          });
        }
      }
    }
  }, [contractData.tipContract, contractData.dataContract]);

  useEffect(() => {
    if (!contractData.numarContract || contractData.numarContract.length < 5) {
      setIsLoading(false); // evită blocarea pe "Se încarcă..."
      return;
    }
    setIsLoading(true);
    fetchContractStatus(maxRetries);
  }, [contractData.numarContract]);

  // Funcțiile de handle pentru câmpurile locale
  const handleContCurentChange = (event) => {
    const { value } = event.target;
    setLocalContCurent(value);
    handleInputChange({ target: { name: "contCurent", value } });
  };

  const handleNumeTitularContChange = (event) => {
    const { value } = event.target;
    setLocalNumeTitularCont(value);
    handleInputChange({ target: { name: "numeTitularCont", value } });
  };

  const handleBancaChange = (event) => {
    const { value } = event.target;
    setLocalBanca(value);
    handleInputChange({ target: { name: "banca", value } });
  };

  // Condiții pentru ascunderea câmpurilor
  const isLP001 =
    contractData.tipContract && contractData.tipContract.includes("LP001");
  const isLP002 =
    contractData.tipContract && contractData.tipContract.includes("LP002");
  const isLP003 =
    contractData.tipContract && contractData.tipContract.includes("LP003");
  const isLP004 =
    contractData.tipContract && contractData.tipContract.includes("LP004");
  const isLP005 =
    contractData.tipContract && contractData.tipContract.includes("LP005");
  const isLP006 =
    contractData.tipContract && contractData.tipContract.includes("LP006");
  const isZohoAllowed = isLP001 || isLP002 || isLP003 || isLP004 || isLP005;

  useEffect(() => {
    setDidToggleTier(false);
  }, [contractData.tipContract]);

  useEffect(() => {
    if (!isZohoAllowed) {
      if (contractTier !== "premium") {
        setContractTier("premium");
      }
      return;
    }

    if (!didToggleTier && contractTier !== "basic") {
      setContractTier("basic");
    }
  }, [isZohoAllowed, contractTier, didToggleTier]);

  // Funcție pentru calcularea datei finale, ajustată pentru weekend
  const handleConsignatiePerioadaChange = (event) => {
    const days = parseInt(event.target.value, 10);
    const months = days / 30;
    const newEndDate = calculateEndDate(months);
    handleInputChange(event);
    handleInputChange({ target: { name: "panaCand", value: newEndDate } });
  };

  const calculateEndDate = (months) => {
    const dataContract = new Date(contractData.dataContract);
    if (!isNaN(dataContract) && months) {
      let endDate = new Date(dataContract);
      endDate.setMonth(dataContract.getMonth() + months);

      // Verificăm dacă data expirării cade într-un weekend
      const dayOfWeek = endDate.getDay();
      if (dayOfWeek === 6) {
        // Sâmbătă -> mutăm la luni
        endDate.setDate(endDate.getDate() + 2);
      } else if (dayOfWeek === 0) {
        // Duminică -> mutăm la luni
        endDate.setDate(endDate.getDate() + 1);
      }

      return endDate.toISOString().split("T")[0]; // Formatăm în yyyy-mm-dd
    }
    return "";
  };

  const handlePretCuvenitChange = (e) => {
    setLocalPretCuvenit(e.target.value);
    handleInputChange({
      target: { name: "pretCuvenit", value: e.target.value },
    });
  };
  const handleCategoriaChange = (e) => {
    setLocalCategoria(e.target.value);
    handleInputChange({ target: { name: "categoria", value: e.target.value } });
  };
  const handleBrandChange = (e) => {
    setLocalBrand(e.target.value);
    handleInputChange({ target: { name: "brand", value: e.target.value } });
  };
  const handleModelChange = (e) => {
    setLocalModel(e.target.value);
    handleInputChange({ target: { name: "model", value: e.target.value } });
  };

  const handleTierChange = (_event, nextTier) => {
    if (nextTier) {
      setDidToggleTier(true);
      setContractTier(nextTier);
    }
  };

  const buildZohoFields = () => ({
    nrContract: contractData.numarContract,
    dataContract: contractData.dataContract,
    nume: contractData.nume,
    prenume: contractData.prenume,
    adresa: contractData.adresa,
    serieCI: contractData.serieCI,
    nrCI: contractData.nrCI,
    eliberatDe: contractData.eliberatDe,
    eliberatLaData: contractData.eliberatLaData,
    telefon: contractData.telefon,
    email: contractData.email,
    consignatiePerioada: contractData.consignatiePerioada,
    panaCand: contractData.panaCand,
    contCurent: localContCurent,
    banca: localBanca,
    numeTitularCont: localNumeTitularCont,
    categoria: contractData.categoria,
    brand: contractData.brand,
    model: contractData.model,
    codInteriorProdus: contractData.codInteriorProdus,
    checkboxOptions: insotitDisplay,
    detinFactura: contractData.detinFactura,
    nrFactura: contractData.nrFactura,
    detinCertificat: contractData.detinCertificat,
    serieCertificat: contractData.serieCertificat,
    pretFinal: contractData.pretCuvenit,
    pretFinalVanzare: localPretFinalVanzare,
    dataPlataFinalVanzare: localDataPlataFinalVanzare,
    alteClauze: contractData.alteClauze,
    codVoucher: contractData.codVoucher,
    dataActivareVoucher: contractData.dataActivareVoucher,
    dataDePlata: contractData.dataDePlata,
    um: contractData.um,
    oras: contractData.oras,
    judet: contractData.judet,
  });

  // Funcția pentru trimiterea contractului și setarea statusului
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setMessage("");
    setError(null);

    try {
      const apiBase = "https://server-9p4hx.kinsta.app";

      if (contractTier === "premium") {
        // Construiește payload-ul pentru contract (SignNow)
        const payload = {
          documentName: `${contractData.tipContract}_${contractData.nume}_${contractData.prenume}_${contractData.numarContract}`,
          recipient1Email: contractData.email,
          recipient1Name: `${contractData.nume} ${contractData.prenume}`,
          recipientPhone: contractData.telefon,
          Tip_Contract: contractData.tipContract,
          nrContract: contractData.numarContract,
          smartFieldsData: [
            { nume: contractData.nume },
            { prenume: contractData.prenume },
            { telefon: contractData.telefon },
            { email: contractData.email },
            { adresa: contractData.adresa },
            { oras: contractData.oras },
            { judet: contractData.judet },
            { serieCI: contractData.serieCI },
            { nrCI: contractData.nrCI },
            { eliberatDe: contractData.eliberatDe },
            { eliberatLaData: contractData.eliberatLaData },
            { NrContract: contractData.numarContract },
            { dataContract: contractData.dataContract },
            { panaCand: contractData.panaCand },
            { consignatiePerioada: contractData.consignatiePerioada },
            { tipContract: contractData.tipContract },
            { banca: localBanca },
            { contCurent: localContCurent },
            { numeTitularCont: localNumeTitularCont },
            { pretFinal: contractData.pretCuvenit },
            { codVoucher: contractData.codVoucher },
            { dataActivareVoucher: contractData.dataActivareVoucher },
            { dataDePlata: contractData.dataDePlata },
            { pretFinalVanzare: contractData.pretFinalVanzare },
            { dataPlataFinalVanzare: contractData.dataPlataFinalVanzare },
            { um: contractData.um },
            { categoria: contractData.categoria },
            { brand: contractData.brand },
            { model: contractData.model },
            { codInteriorProdus: contractData.codInteriorProdus },
            { checkboxOptions: contractData.checkboxOptions },
            { detinFactura: contractData.detinFactura },
            { nrFactura: contractData.nrFactura },
            { detinCertificat: contractData.detinCertificat },
            { alteClauze: contractData.alteClauze },
            { consignatar: "Mirela Bucurei" },
            { serieCertificat: contractData.serieCertificat },
            { pretFinalVanzare: localPretFinalVanzare },
            { dataPlataFinalVanzare: localDataPlataFinalVanzare },
          ],
        };

        const contractResponse = await axios.post(
          `${apiBase}/api/contracts/signnow/workflow`,
          payload,
        );
        console.log("Contract sent (SignNow):", contractResponse.data);
      } else {
        // Payload Zoho
        const zohoTemplateIdByCode = {
          LP001: "104126000000048747",
          LP002: "104126000000048867",
          LP003: "104126000000048991",
          LP004: "104126000000050121",
          LP005: "104126000000047732",
        };
        const tipContract = String(contractData.tipContract || "");
        const zohoTemplateId =
          Object.entries(zohoTemplateIdByCode).find(([code]) =>
            tipContract.includes(code),
          )?.[1] || null;

        if (!zohoTemplateId) {
          throw new Error(
            "Nu exista template Zoho mapat pentru tipul de contract selectat.",
          );
        }

        const zohoPayload = {
          fields: buildZohoFields(),
          signer_email: contractData.email,
          signer_name: `${contractData.nume} ${contractData.prenume}`,
          request_name: `${contractData.tipContract}_${contractData.nume}_${contractData.prenume}_${contractData.numarContract}`,
          auto_submit: true,
        };
        const zohoResponse = await axios.post(
          `${apiBase}/api/contracte/zoho/templates/${zohoTemplateId}/create`,
          zohoPayload,
        );
        console.log("Contract sent (Zoho):", zohoResponse.data);
      }

      // Actualizare status CONT in Jira (aceeasi logica pentru Premium si Basic)
      const lastFiveCharacters = contractData.numarContract.slice(-5);
      if (!lastFiveCharacters) {
        throw new Error(
          "Numărul contractului nu este valid pentru actualizarea statusului.",
        );
      }
      const statusPayload = { status: "22_CREAT" };
      const statusResponse = await axios.post(
        buildStatusApiUrl(`/api/tasks/set-status/${lastFiveCharacters}`),
        statusPayload,
      );
      console.log("Status updated:", statusResponse.data);

      setMessage(
        contractTier === "premium"
          ? "Contractul a fost trimis prin SignNow, iar statusul CONT a fost actualizat."
          : "Contractul a fost trimis prin Zoho, iar statusul CONT a fost actualizat.",
      );
    } catch (error) {
      console.error(
        "Eroare la trimiterea contractului sau actualizarea statusului:",
        error,
      );
      setError(
        error.response?.data?.error ||
          "A apărut o eroare la trimiterea contractului sau actualizarea statusului.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <Typography>Se încarcă...</Typography>;
  }

  if (status === "22_CREAT") {
    return (
      <Alert severity="warning" sx={{ marginTop: 2 }}>
        Contractul a fost creat, așteptăm semnătura de la vendor și de la
        Luxura.
      </Alert>
    );
  }

  const handlePretFinalVanzareChange = (event) => {
    const { value } = event.target;
    setLocalPretFinalVanzare(value);
    handleInputChange({ target: { name: "pretFinalVanzare", value } });
  };

  const handleDataPlataFinalVanzareChange = (event) => {
    const { value } = event.target;
    setLocalDataPlataFinalVanzare(value);
    handleInputChange({ target: { name: "dataPlataFinalVanzare", value } });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        background: contractTier === "premium" ? "#f7d6f4" : "#dbeeff",
        borderRadius: 3,
        padding: { xs: 2, md: 3 },
        boxShadow: "0 16px 40px rgba(0, 0, 0, 0.08)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
          padding: 2,
          borderRadius: 2,
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          backdropFilter: "blur(6px)",
        }}
      >
        <ToggleButtonGroup
          value={contractTier}
          exclusive
          onChange={handleTierChange}
          disabled={!isZohoAllowed}
          size="small"
          sx={{
            backgroundColor: "#ffffff",
            borderRadius: 999,
            padding: "4px",
            boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)",
            "& .MuiToggleButton-root": {
              border: "none",
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 700,
              paddingX: 2.5,
              color: "#2d2d2d",
            },
            "& .Mui-selected": {
              backgroundColor:
                contractTier === "premium" ? "#c218a3" : "#2d6cdf",
              color: "#ffffff",
            },
            "& .Mui-selected:hover": {
              backgroundColor:
                contractTier === "premium" ? "#a5148c" : "#2458b7",
            },
          }}
        >
          <ToggleButton value="premium">Premium</ToggleButton>
          <ToggleButton value="basic">Basic</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {/* Date Vanzator Section */}
      <Typography variant="h6">Date Vanzator</Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            label="Nume"
            name="nume"
            variant="outlined"
            fullWidth
            value={contractData.nume}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Prenume"
            name="prenume"
            variant="outlined"
            fullWidth
            value={contractData.prenume}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Telefon"
            name="telefon"
            variant="outlined"
            fullWidth
            value={contractData.telefon}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Email"
            name="email"
            variant="outlined"
            fullWidth
            value={contractData.email}
            onChange={handleInputChange}
          />
        </Grid>
      </Grid>
      <Box sx={{ border: "1px solid #1E90FF", padding: 2, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Adresa"
              name="adresa"
              variant="outlined"
              fullWidth
              value={contractData.adresa}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Oras"
              name="oras"
              variant="outlined"
              fullWidth
              value={contractData.oras}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Judet"
              name="judet"
              variant="outlined"
              fullWidth
              value={contractData.judet}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="CI Seria"
              name="serieCI"
              variant="outlined"
              fullWidth
              value={contractData.serieCI}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="CI Numar"
              name="nrCI"
              variant="outlined"
              fullWidth
              value={contractData.nrCI}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Eliberat de"
              name="eliberatDe"
              variant="outlined"
              fullWidth
              value={contractData.eliberatDe}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Eliberat la data"
              name="eliberatLaData"
              type="date"
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              fullWidth
              value={contractData.eliberatLaData}
              onChange={handleInputChange}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Date Contract Section */}
      <Typography variant="h6">Date Contract</Typography>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <TextField
            label="Numar Contract"
            name="numarContract"
            variant="outlined"
            fullWidth
            value={contractData.numarContract}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Data Contract"
            name="dataContract"
            type="date"
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            fullWidth
            value={contractData.dataContract}
            onChange={handleInputChange}
          />
        </Grid>
        {!isLP004 && !isLP003 && !isLP005 && !isLP006 && (
          <Grid item xs={4}>
            <TextField
              label="Pana cand"
              name="panaCand"
              type="date"
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              fullWidth
              helperText="Ultima zi cand expira contractul sau facem plata."
              value={contractData.panaCand}
              onChange={handleInputChange}
            />
          </Grid>
        )}
        {!isLP004 && !isLP003 && !isLP005 && !isLP006 && (
          <Grid item xs={4}>
            <FormControl fullWidth>
              <InputLabel>Consignatie perioada</InputLabel>
              <Select
                name="consignatiePerioada"
                value={contractData.consignatiePerioada || ""}
                onChange={handleConsignatiePerioadaChange}
                label="Consignatie perioada"
              >
                <MenuItem value={30}>30</MenuItem>
                <MenuItem value={60}>60</MenuItem>
                <MenuItem value={90}>90</MenuItem>
                <MenuItem value={180}>180</MenuItem>
                <MenuItem value={270}>270</MenuItem>
                <MenuItem value={360}>360</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={6}>
          <TextField
            label="TIP Contract"
            name="tipContract"
            variant="outlined"
            fullWidth
            value={contractData.tipContract}
            onChange={handleInputChange}
          />
        </Grid>
      </Grid>

      {/* Detalii Plata Section */}
      <Typography variant="h6">Detalii Plata</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Banca"
            name="banca"
            variant="outlined"
            fullWidth
            value={localBanca || ""}
            onChange={handleBancaChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Cont Curent (IBAN)"
            name="contCurent"
            variant="outlined"
            fullWidth
            value={localContCurent} // Folosim valoarea locală
            onChange={handleContCurentChange} // Actualizare locală
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Numele titularului de cont"
            name="numeTitularCont"
            variant="outlined"
            fullWidth
            value={localNumeTitularCont}
            onChange={handleNumeTitularContChange}
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            label="Prețul cuvenit consignantului"
            name="pretCuvenit"
            variant="outlined"
            fullWidth
            value={localPretCuvenit}
            onChange={handlePretCuvenitChange}
            InputProps={{
              endAdornment: (
                <span style={{ color: "green", fontWeight: "bold" }}>RON</span>
              ),
              style: { color: "green", fontWeight: "bold" },
            }}
          />
        </Grid>
        {!isLP001 && !isLP002 && !isLP004 && !isLP005 && !isLP006 && (
          <>
            <Grid item xs={3}>
              <TextField
                label="Cod Voucher"
                name="codVoucher"
                variant="outlined"
                fullWidth
                value={contractData.codVoucher || ""}
                onChange={handleInputChange}
              />
            </Grid>
            {!isLP001 &&
              !isLP002 &&
              !isLP004 &&
              !isLP005 &&
              !isLP006 &&
              !isLP003 && (
                <Grid item xs={3}>
                  <TextField
                    label="Data activarii voucher"
                    name="dataActivareVoucher"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    fullWidth
                    value={contractData.dataActivareVoucher || ""}
                    onChange={handleInputChange}
                  />
                </Grid>
              )}
          </>
        )}
        {!isLP001 && !isLP002 && !isLP003 && !isLP004 && !isLP006 && (
          <Grid item xs={12}>
            <TextField
              label="Data de Plata"
              name="dataDePlata"
              type="date"
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              fullWidth
              value={contractData.dataDePlata || ""}
              onChange={handleInputChange}
            />
          </Grid>
        )}

        {/* Chenar Verde - Prețul și Data Plății la Final */}
        {!isLP001 && !isLP003 && !isLP004 && !isLP005 && !isLP006 && (
          <Grid item xs={12}>
            <Box
              sx={{
                border: "2px solid green",
                padding: 2,
                borderRadius: 2,
                mt: 2,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Prețul la final - Vanzare"
                    name="pretFinalVanzare"
                    variant="outlined"
                    fullWidth
                    value={localPretFinalVanzare}
                    onChange={handlePretFinalVanzareChange}
                    sx={{ color: "green" }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Data platii la final - Vanzare"
                    name="dataPlataFinalVanzare"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    fullWidth
                    value={localDataPlataFinalVanzare}
                    onChange={handleDataPlataFinalVanzareChange}
                    sx={{ color: "green" }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Detalii Produs Section */}
      <Typography variant="h6">Detalii Produs</Typography>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <TextField
            label="U/M"
            name="um"
            variant="outlined"
            fullWidth
            required
            value={contractData.um || "1 BUC"}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Categoria"
            name="categoria"
            variant="outlined"
            fullWidth
            value={contractData.categoria || ""}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Brand"
            name="brand"
            variant="outlined"
            fullWidth
            value={contractData.brand || ""}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Model"
            name="model"
            variant="outlined"
            fullWidth
            value={contractData.model || ""}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Cod interior produs"
            name="codInteriorProdus"
            variant="outlined"
            fullWidth
            required
            value={contractData.codInteriorProdus}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Insotit cu"
            name="checkboxOptions"
            variant="outlined"
            fullWidth
            value={insotitDisplay}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>Detin Factura / Bon</InputLabel>
            <Select
              name="detinFactura"
              value={contractData.detinFactura || ""}
              onChange={(e) => {
                handleInputChange(e);
                if (
                  e.target.value !==
                  "Vine insotita de factura in original aferenta achizitiei"
                ) {
                  handleInputChange({
                    target: { name: "nrFactura", value: "" },
                  });
                }
              }}
              label="Detin Factura / Bon"
            >
              <MenuItem value="Vine insotita de factura in original aferenta achizitiei">
                Vine insotita de factura in original aferenta achizitiei
              </MenuItem>
              <MenuItem value="Nu vine insotit de factura">
                Nu vine insotit de factura
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {contractData.detinFactura ===
          "Vine insotita de factura in original aferenta achizitiei" && (
          <Grid item xs={6}>
            <TextField
              label="Nr Factura"
              name="nrFactura"
              variant="outlined"
              fullWidth
              value={contractData.nrFactura}
              onChange={handleInputChange}
            />
          </Grid>
        )}
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>Detin Certificat / Card</InputLabel>
            <Select
              name="detinCertificat"
              value={contractData.detinCertificat || ""}
              onChange={(e) => {
                handleInputChange(e);
                if (e.target.value !== "DA") {
                  handleInputChange({
                    target: { name: "serieCertificat", value: "" },
                  });
                }
              }}
              label="Detin Certificat / Card"
            >
              <MenuItem value="DA">DA</MenuItem>
              <MenuItem value="NU">NU</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {contractData.detinCertificat === "DA" && (
          <Grid item xs={6}>
            <TextField
              label="Serie Certificat / Card"
              name="serieCertificat"
              variant="outlined"
              fullWidth
              value={contractData.serieCertificat}
              onChange={handleInputChange}
            />
          </Grid>
        )}
      </Grid>

      {/* Detalii Alte clauze Section */}
      <Typography variant="h6">Alte Clauze</Typography>
      <Grid container spacing={1} sx={{ width: "100%" }}>
        <Grid item xs={12}>
          <TextField
            label="Alte Clauze"
            name="alteClauze"
            variant="outlined"
            fullWidth
            required
            multiline
            rows={4} // sau mai multe rânduri, după preferință
            value={contractData.alteClauze || ""}
            onChange={handleInputChange}
          />
        </Grid>
      </Grid>
      {/* Afișarea mesajului de succes sau eroare */}

      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        disabled={isSubmitting}
      >
        Trimite Contract
      </Button>

      {/* Feedback pentru Basic, sub buton */}
      {contractTier === "basic" && (message || error) && (
        <Box
          sx={{
            mt: 2,
            px: 2,
            py: 1.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: error
              ? "rgba(211, 47, 47, 0.25)"
              : "rgba(25, 118, 210, 0.25)",
            backgroundColor: error
              ? "rgba(211, 47, 47, 0.08)"
              : "rgba(25, 118, 210, 0.08)",
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: error ? "#c62828" : "#1565c0", fontWeight: 600 }}
          >
            {error || message}
          </Typography>
        </Box>
      )}
      {/* Nu mai afișăm eroarea de fetch status contract în UI */}
    </Box>
  );
};

const CreareContractForm = (props) => isLp006Contract(props.contractData?.tipContract)
  ? <Lp006ContractForm key={props.issueId} {...props} />
  : <LegacyCreareContractForm {...props} />;

export default CreareContractForm;
